"use client"

import React, {Suspense, Usable, useMemo, useState} from "react";
import {useIsMobile} from "@/hooks/use-mobile";
import {Link} from '@/i18n/navigation';
import {useDebounce} from "use-debounce";
import {useGetGamesInfiniteQuery} from "@/lib/api/games/gameApi";
import {Skeleton} from "@/app/components/common/app-skeleton/AppSkeleton";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import {AppFooter} from "@/app/components/common/app-footer/AppFooter";
import NothingFound from "@/app/components/common/nothing-found/NothingFound";
import GameCard from "@/app/components/game/game-card/GameCard";
import LinkedGoBackHeader from "@/app/components/common/go-back-header/LinkedGoBackHeader";
import SearchInput from "@/app/components/common/search-input/SearchInput";
import {useTranslations} from "next-intl";
import {useAppSelector} from "@/lib/hooks";
import {AuthTypesEnum} from "@/lib/enums/AuthTypesEnum";
// import dynamic from "next/dynamic";
import {calcAmountOfCards} from "@/lib/utils/functions/calcAmountOfCards";
import {TailwindBreakpointsEnum} from "@/lib/enums/TailwindBreakpointsEnum";
import {useOpenDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {rememberGameOpenSource} from "@/lib/analytics/mixpanel-events";
import {IGame} from "@/lib/types/games/IGame";
import {useLobbyCountry} from "@/hooks/use-lobby-country";

interface IProps {
    params: Usable<{
        category: string;
    }>
}

const amountOfCards = calcAmountOfCards({
    [TailwindBreakpointsEnum.XS]: 36,
    [TailwindBreakpointsEnum.SM]: 36,
    [TailwindBreakpointsEnum.MD]: 35,
    [TailwindBreakpointsEnum.LG]: 36,
    [TailwindBreakpointsEnum.XL]: 35,
    [TailwindBreakpointsEnum.TWO_XL]: 32,
})

// const AuthDialog = dynamic(() => import("@/app/components/auth/AuthDialog"));

export default function Category({params}: IProps) {
    const {category} = React.use(params);
    const t = useTranslations();
    const isMobile = useIsMobile();
    const [search, setSearch] = useState('');
    const [debouncedSearchTerm] = useDebounce(search, 500);
    const openDialog = useOpenDialog();
    const isAuthorized = useAppSelector(state => !!state.auth.userId);
    const userCountry = useAppSelector(state => state.auth.country);
    const lobbyCountry = useLobbyCountry();

    const {
        data: games,
        isFetching: gamesFetching,
        hasNextPage: gamesHasNextPage,
        fetchNextPage: gamesFetchNextPage
    } = useGetGamesInfiniteQuery({
        category: category === 'popular' ? undefined : category,
        title: debouncedSearchTerm.trim() ? debouncedSearchTerm.trim() : undefined,
        size: amountOfCards,
        isTop: category === 'popular',
        country: lobbyCountry,
    })

    const allGames = useMemo(() => {
        return games?.pages?.flatMap(page => page.content || []) || [];
    }, [games]);

    const isNoRecords = useMemo(() => {
        return !allGames.length && !gamesFetching;
    }, [allGames.length, gamesFetching])

    const onCardClick = (item: IGame) => {
        if (!isAuthorized) {
            openDialog(DialogsEnum.AUTH, {props: {tab: AuthTypesEnum.REGISTER}});
            return;
        }
        // Restricted games link to '#': no navigation, so no game open to attribute.
        if (!item.producer.restrictedCountries.includes(userCountry)) {
            rememberGameOpenSource("casino");
        }
    }

    return (
        <div className={'px-[16px] pt-[24px] md:px-[32px] bg-dark-gray scroll-mt-[100px]'}>

            <div className={'flex flex-col mb-[24px] sm:mb-[16px] md:mb-[24px] gap-[16px] md:flex-row'}>
                <LinkedGoBackHeader iconBtnSize={isMobile ? 'md' : 'lg'} className={'gap-[12px] overflow-hidden flex-1'}
                                    title={category.replace("-", " ")}/>

                <SearchInput value={search} onChange={(value) => setSearch(value.target.value)}
                             className={'gap-[8px] px-[12px] flex-1 md:h-[41px] md:basis-[395px] md:grow-0'}/>
            </div>

            {
                isNoRecords
                    ? (
                        <div
                            className={'h-[calc(100vh-271px)] sm:h-[calc(100vh-263px)] md:h-[calc(100vh-172px)] flex items-center justify-center'}>
                            <NothingFound/>
                        </div>
                    ) : (
                        <div className={'min-h-dvh'}>
                            <div
                                className={'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-[16px]'}>
                                {allGames.map((item) => (
                                    <Link prefetch={false} key={item.id}
                                          href={!isAuthorized || item.producer.restrictedCountries.includes(userCountry) ? '#' : `/games/${item.id}`}
                                          onClick={() => onCardClick(item)}>
                                        <GameCard
                                            isUnavailable={item.producer.restrictedCountries.includes(userCountry)}
                                            poster={item.previewUrl}
                                            sizes={'(max-width: 639px) 30vw, (max-width: 767px) 23vw, (max-width: 1023px) 18vw, (max-width: 1279px) 15vw, (max-width: 1535px) 13vw, 12vw'}
                                            gameName={item.title}
                                            providerName={item.producer.title}
                                        />
                                    </Link>
                                ))}
                                {
                                    gamesFetching ? Array.from({length: 36}).map((_, index) => (
                                        <Skeleton key={index} className={'aspect-3/4 bg-noble-black-600'}/>
                                    )) : null
                                }
                            </div>

                            {
                                gamesHasNextPage && (
                                    <MainCtaBtn onClick={gamesFetchNextPage}
                                                className={'mx-auto block my-[32px] w-[280px] h-[41px] py-[12px]'}>
                                        {t('AppBtn.loadMore')}
                                    </MainCtaBtn>
                                )
                            }
                        </div>
                    )
            }

            <Suspense fallback={null}>
                <AppFooter/>
            </Suspense>
        </div>
    )
}
